import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { Page, PageHeader } from "@/components/PageHeader";
import { PaperPlaneTilt, Robot, User, CheckCircle, WarningCircle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { initials, timeAgo } from "@/lib/format";

const statusStyle = {
  active: "bg-success/10 text-success border-success/20",
  escalated: "bg-amber-50 text-amber-600 border-amber-200",
  resolved: "bg-secondary text-muted-foreground border-border",
};

export default function Chat() {
  const qc = useQueryClient();
  const { data: convos = [] } = useQuery({ queryKey: ["conversations"], queryFn: async () => (await api.get("/conversations")).data, refetchInterval: 5000 });
  const [selected, setSelected] = useState(null);
  const [text, setText] = useState("");

  useEffect(() => {
    if (!selected && convos.length) setSelected(convos[0].id);
  }, [convos, selected]);

  const active = convos.find((c) => c.id === selected);
  const isHuman = active?.agent_mode === "human";

  const send = useMutation({
    mutationFn: async () => (await api.post(`/conversations/${selected}/message`, { text, as_ai: false })).data,
    onSuccess: () => { setText(""); qc.invalidateQueries({ queryKey: ["conversations"] }); },
  });

  const setStatus = useMutation({
    mutationFn: async (status) => (await api.post(`/conversations/${selected}/status`, { status })).data,
    onSuccess: (_, status) => { qc.invalidateQueries({ queryKey: ["conversations"] }); toast.success(`Marked as ${status}`); },
  });

  const handoff = useMutation({
    mutationFn: async (mode) => (await api.post(`/conversations/${selected}/handoff`, { mode })).data,
    onSuccess: (_, mode) => { qc.invalidateQueries({ queryKey: ["conversations"] }); toast.success(mode === "human" ? "You've taken over this chat" : "Handed back to AI concierge"); },
  });

  return (
    <Page>
      <PageHeader title="AI Chat" subtitle="Digital concierge inbox — grounded answers with human escalation." />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 h-[calc(100vh-190px)]">
        {/* conversation list */}
        <div className="rounded-lg border border-border bg-card overflow-hidden flex flex-col">
          <div className="px-4 py-3 border-b border-border text-[11px] uppercase tracking-[0.12em] font-semibold text-muted-foreground">Conversations · {convos.length}</div>
          <div className="overflow-y-auto flex-1 divide-y divide-border">
            {convos.map((c, i) => (
              <button key={c.id} data-testid={`conversation-${i}`} onClick={() => setSelected(c.id)} className={`w-full text-left px-4 py-3 hover:bg-secondary/50 transition-colors ${selected === c.id ? "bg-secondary" : ""}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-sm flex items-center gap-1.5">
                    {c.channel === "sms" && <span data-testid="channel-badge-sms" className="text-[9px] px-1.5 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20 font-semibold">SMS</span>}
                    {c.guest_name}
                  </span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full border capitalize ${statusStyle[c.status]}`}>{c.status}</span>
                </div>
                <p className="text-xs text-muted-foreground truncate mt-1">{c.messages[c.messages.length - 1]?.text}</p>
                <span className="text-[11px] text-muted-foreground">{timeAgo(c.created_at)}</span>
              </button>
            ))}
          </div>
        </div>

        {/* thread */}
        <div className="lg:col-span-2 rounded-lg border border-border bg-card flex flex-col overflow-hidden">
          {active ? (
            <>
              <div className="px-5 py-3.5 border-b border-border flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="h-9 w-9 rounded-full bg-primary/10 text-primary flex items-center justify-center font-medium text-sm">{initials(active.guest_name)}</div>
                  <div>
                    <div className="font-medium text-sm">{active.guest_name}</div>
                    <div className="text-xs text-muted-foreground flex items-center gap-1.5">
                      via {active.channel === "sms" ? "SMS" : "widget"} ·
                      {isHuman
                        ? <span data-testid="agent-mode-badge" className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full border bg-foreground text-background border-foreground">You're handling · Live</span>
                        : <span data-testid="agent-mode-badge" className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full border bg-primary/10 text-primary border-primary/20"><Robot size={11} weight="fill" /> AI concierge</span>}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  {isHuman
                    ? <Button data-testid="handback-btn" variant="outline" size="sm" className="gap-1.5" onClick={() => handoff.mutate("ai")}><Robot size={15} weight="fill" /> Hand back to AI</Button>
                    : <Button data-testid="takeover-btn" size="sm" className="gap-1.5" onClick={() => handoff.mutate("human")}><User size={15} weight="fill" /> Take over</Button>}
                  {active.status !== "resolved" && <Button data-testid="resolve-btn" variant="outline" size="sm" className="gap-1.5" onClick={() => setStatus.mutate("resolved")}><CheckCircle size={15} /> Resolve</Button>}
                  {active.status !== "escalated" && <Button data-testid="escalate-btn" variant="secondary" size="sm" className="gap-1.5" onClick={() => setStatus.mutate("escalated")}><WarningCircle size={15} /> Escalate</Button>}
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-5 space-y-3.5 bg-secondary/20">
                {active.messages.map((m, i) => {
                  if (m.role === "system") {
                    return (
                      <div key={i} className="flex justify-center">
                        <span className="text-[11px] text-muted-foreground bg-card border border-border rounded-full px-3 py-1">{m.text}</span>
                      </div>
                    );
                  }
                  const guest = m.role === "guest";
                  return (
                    <div key={i} className={`flex gap-2.5 ${guest ? "flex-row" : "flex-row-reverse"}`}>
                      <div className={`h-7 w-7 rounded-full flex items-center justify-center shrink-0 ${guest ? "bg-secondary text-muted-foreground" : m.role === "ai" ? "bg-primary/10 text-primary" : "bg-foreground text-background"}`}>
                        {guest ? <User size={14} /> : m.role === "ai" ? <Robot size={14} weight="fill" /> : <User size={14} weight="fill" />}
                      </div>
                      <div className={`max-w-[70%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${guest ? "bg-card border border-border rounded-tl-md" : m.role === "ai" ? "bg-primary/5 border border-primary/15 rounded-tr-md" : "bg-foreground text-background rounded-tr-md"}`}>
                        {m.role === "ai" && <div className="text-[10px] font-semibold text-primary mb-0.5">AI Concierge</div>}
                        {m.role === "staff" && <div className="text-[10px] font-semibold opacity-70 mb-0.5">{m.staff_name || "You"}</div>}
                        {m.text}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="p-3 border-t border-border flex items-center gap-2">
                <input data-testid="chat-reply-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && text.trim() && send.mutate()} placeholder={isHuman ? "Reply to the guest…" : "Reply as staff (takes over from AI)…"} className="flex-1 h-10 px-3 rounded-md border border-border text-sm outline-none focus:ring-2 focus:ring-primary/30" />
                <Button data-testid="chat-reply-send" disabled={!text.trim() || send.isPending} onClick={() => send.mutate()} className="gap-1.5"><PaperPlaneTilt size={15} weight="fill" /> Send</Button>
              </div>
            </>
          ) : <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">Select a conversation</div>}
        </div>
      </div>
    </Page>
  );
}
